import { Modal, ModalClose, ModalContent, ModalFooter, ModalHeader, useBCMSModal, useModel } from "@bcm-dev/bcms-vite";
import { PrimaryButton, SecondaryButton } from "components/ui";
import React, { useState } from "react";
import { SVGAsset } from "res";

import "./styles.css";
import "./mobile.css";


const CustomModalContext = React.createContext(null);

/**
 * @function CustomModal
 * @description Main modal component, wraps all modal content
 * @param {STRING} title - The title of the modal
 * @param {STRING} id - The id of the modal
 * @returns {React.Component}
 * @example
 * function TestModal(props) {
 *  return (
 *      <CustomModal id="TestModal" title="Testing">
 *          <CustomModalContent>
 *          </CustomModalContent>
 * 
 *          <CustomModalFooter>
 *              <ModalCloseButton id="TestModal" />
 *              <ModalPrimaryButton text="Continue" />
 *          </CustomModalFooter>
 *      </CustomModal>
 *  );
 * }
 */
export default function CustomModal(props) {
    let className = props.className ?? "";
    const id = props.id ?? "";
    const closePoint = props.dataPoint ? { "data-point": `${ props.dataPoint }_Close` } : {};
    const model = useModel();
    const { branding } = model;
    const { options } = branding;
    const customFont = options.custom_font_abi22;
    let customFontClass = "";
    const hasCustomFont = customFont === "undefined" || customFont === "Default" ? false : true;

    if(hasCustomFont) {
        customFontClass = `${ customFont.toLowerCase() }-font`;
        className = `${ className } ${ customFontClass }`;
    }
    const [ titleState, setTitleState ] = useState(props.title ?? "[Modal Title]");

    const onCloseIconClick = () => {
        if(props.onCloseClick instanceof Function) props.onCloseClick();
    };

    return (
        <CustomModalContext.Provider value={{ titleState, setTitleState }}>
            <Modal className={ `custom-modal ${ className }` }>
                <ModalHeader className="flex-container flex-horizontal">
                    <h3 className="flex-width">{ titleState }</h3>
                    <ModalClose id={ id } className="clickable">
                        <SVGAsset id="CloseX" className="icon" { ...closePoint } onClick={ onCloseIconClick } />
                    </ModalClose>
                </ModalHeader>
                { props.children }
            </Modal>
        </CustomModalContext.Provider>
    );
}

/**
 * @function EDEStatementsCustomModal
 * @description Main ede statements modal component, wraps all modal content.
 * @param {STRING} title - The title of the modal
 * @param {STRING} id - The id of the modal
 * @returns {React.Component}
 * @example
 * function TestModal(props) {
 *  return (
 *      <EDEStatementsCustomModal id="TestModal" title="Testing">
 *          <CustomModalContent>
 *          </CustomModalContent>
 * 
 *          <CustomModalFooter>
 *              <ModalCloseButton id="TestModal" />
 *              <ModalPrimaryButton text="Continue" />
 *          </CustomModalFooter>
 *      </EDEStatementsCustomModal>
 *  );
 * }
 */
export function EDEStatementsCustomModal(props) {
    let className = props.className ?? "";
    const id = props.id ?? "";
    const closePoint = props.dataPoint ? { "data-point": `${ props.dataPoint }_Close` } : {};
    const model = useModel();
    const { branding } = model;
    const { options } = branding;
    const customFont = options.custom_font_abi22;
    let customFontClass = "";
    const hasCustomFont = customFont === "undefined" || customFont === "Default" ? false : true;

    if(hasCustomFont) {
        customFontClass = `${ customFont.toLowerCase() }-font`;
        className = `${ className } ${ customFontClass }`;
    }
    const [ titleState, setTitleState ] = useState(props.title ?? "[Modal Title]");

    const onCloseIconClick = () => {
        if(props.onCloseClick instanceof Function) props.onCloseClick();
    };

    return (
        <CustomModalContext.Provider value={{ titleState, setTitleState }}>
            <Modal className={ `custom-modal ede-statements ${ className }` }>
                <ModalHeader className="flex-container flex-horizontal">
                    <h3 className="flex-width modal-title">{ titleState }</h3>
                    <ModalClose id={ id } className="clickable modal-close-icon-container">
                        <SVGAsset id="CloseX" className="icon inline-text-icon right-icon" { ...closePoint } onClick={ onCloseIconClick } />
                    </ModalClose>
                </ModalHeader>
                { props.children }
            </Modal>
        </CustomModalContext.Provider>
    );
}

export const useCustomModal = () => React.useContext(CustomModalContext);

/**
 * @function CustomModalContent
 * @description Modal Content component
 * @param {STRING} className - Additional class that will be added
 * @returns {React.Component}
 * @example
 * <CustomModalContent className="SomeClass">
 *  ...
 * </CustomModalContent>
 */
export function CustomModalContent(props) {
    return (
        <ModalContent>
            { props.children }
        </ModalContent>
    );
}

/**
 * @function CustomModalFooter
 * @description Modal footer component
 * @param {STRING} className - Additional class that will be added
 * @returns {React.Component}
 * @example
 * <CustomModalFooter className="SomeClass">
 *  ...
 * </CustomModalFooter>
 */
export function CustomModalFooter(props) {
    const className = props.className ?? "";

    return (
        <ModalFooter className={ `flex-container flex-horizontal ${ className }` }>
            { props.children }
        </ModalFooter>
    );
}

/**
 * @function ModalSecondaryButton
 * @param {STRING} text - Allows us to set the button text.
 * @param {FUNCTION} onClick - Allows us to set an event handler for the button when clicked.
 * @returns {React.Component}
 * @example
 * <ModalSecondaryButton text="Some Text" onClick={ () => undefined } />
 */
export function ModalSecondaryButton(props) {
    const text = props.text ?? "";
    const onClick = props.onClick ?? null;

    return <SecondaryButton text={ text } onClick={ onClick } dataPoint={ props.dataPoint } />
}

/**
 * @function ModalPrimaryButton
 * @param {STRING} text - Allows us to set the button text.
 * @param {FUNCTION} onClick - Allows us to set an event handler for the button when clicked.
 * @param {STRING} dataPoint - Data point that should be applied
 * @returns {React.Component}
 * @example
 * <ModalPrimaryButton text="Some Text" onClick={ () => undefined } />
 */
export function ModalPrimaryButton(props) {
    const text = props.text ?? "";
    const onClick = props.onClick ?? null;
    const point = props.dataPoint ?? "";

    return <PrimaryButton text={ text } onClick={ onClick } data-point={ point } />
}

/**
 * @function ModalCloseButton
 * @param {STRING} id - The id of the modal that should be closed
 * @returns {React.Component}
 * @example
 * <ModalCloseButton id="TestModal" />
 */
export function ModalCloseButton(props) {
    const { close } = useBCMSModal();
    return <SecondaryButton text="Cancel" onClick={ () => close(props.id) } />;
}